/**
 * Pure Minecraft-import logic for the map editor — NO DOM, NO three.js, so
 * `agent/bench-mcimport.mjs` can compile and drive it headless. editorScreen.ts
 * owns everything interactive (file reading, decompression streams, scene).
 */

/** Minimal big-endian NBT reader — enough for classic schematics and modern chunks. */
export function parseNBT(buf: ArrayBuffer): Record<string, unknown> {
  const dv = new DataView(buf); let off = 0;
  const u8 = (): number => dv.getUint8(off++);
  const i16 = (): number => { const v = dv.getInt16(off); off += 2; return v; };
  const i32 = (): number => { const v = dv.getInt32(off); off += 4; return v; };
  const str = (): string => { const n = dv.getUint16(off); off += 2; let s = ""; for (let i = 0; i < n; i++) s += String.fromCharCode(dv.getUint8(off++)); return s; };
  const payload = (tag: number): unknown => {
    switch (tag) {
      case 1: return dv.getInt8(off++);
      case 2: return i16();
      case 3: return i32();
      case 4: { off += 8; return 0; } // long — unused
      case 5: { const v = dv.getFloat32(off); off += 4; return v; }
      case 6: { const v = dv.getFloat64(off); off += 8; return v; }
      case 7: { const n = i32(); const a = new Uint8Array(buf, off, n); off += n; return a; }
      case 8: return str();
      case 9: { const t = u8(); const n = i32(); const a: unknown[] = []; for (let i = 0; i < n; i++) a.push(payload(t)); return a; }
      case 10: { const o: Record<string, unknown> = {}; for (;;) { const t = u8(); if (t === 0) break; const nm = str(); o[nm] = payload(t); } return o; }
      case 11: { const n = i32(); const a = new Int32Array(n); for (let i = 0; i < n; i++) a[i] = i32(); return a; }
      case 12: { const n = i32(); const a: bigint[] = []; for (let i = 0; i < n; i++) { a.push(dv.getBigInt64(off)); off += 8; } return a; }
      default: throw new Error("bad NBT tag " + tag);
    }
  };
  if (u8() !== 10) throw new Error("not an NBT compound");
  str();
  return payload(10) as Record<string, unknown>;
}

/** Rough color for a classic block id (0 = air). */
export function mcBlockColor(id: number): string {
  const map: Record<number, string> = { 1: "#8a8a8a", 2: "#6ab04c", 3: "#9c6b3f", 4: "#787878", 5: "#b8863b", 7: "#3a3a3a", 12: "#e0d7a0", 17: "#6e4a25", 18: "#3f8f3a", 20: "#bfe6ef", 24: "#e6dca8", 35: "#d0d0d0", 41: "#f7d94c", 45: "#9c5a4a", 87: "#7a2b2b", 89: "#e8c76a", 98: "#9a9a9a", 155: "#eee" };
  return map[id] ?? `hsl(${(id * 47) % 360},40%,55%)`;
}

const MC_COLORS: { test: RegExp; color: string }[] = [
  // dye-coloured blocks first so e.g. blue_terracotta reads blue, not clay-brown
  { test: /light_blue/i, color: "#7fb8e0" },
  { test: /light_gray/i, color: "#9aa0a0" },
  { test: /blue_|lapis/i, color: "#3b5bd0" },
  { test: /gray_/i, color: "#5f6468" },
  { test: /lime/i, color: "#7ec84a" },
  { test: /pink/i, color: "#e08fb2" },
  { test: /magenta/i, color: "#c05fc9" },
  { test: /purple|purpur|amethyst/i, color: "#9a5cd0" },
  { test: /orange_|pumpkin/i, color: "#d98a2f" },
  { test: /brown/i, color: "#7a5230" },
  { test: /green_/i, color: "#4f7a2f" },
  { test: /netherrack|nether_brick|crimson/i, color: "#8a3a3a" },
  { test: /warped/i, color: "#2fa08a" },
  { test: /copper/i, color: "#c76f42" },
  { test: /grass|moss|leaves|vine|azalea/i, color: "#5f9d45" },
  { test: /dirt|mud|podzol|rooted/i, color: "#8b5a35" },
  { test: /sand|end_stone/i, color: "#d8c984" },
  { test: /stone|deepslate|andesite|granite|diorite|tuff|calcite/i, color: "#858585" },
  { test: /cobble|brick|terracotta/i, color: "#9a786a" },
  { test: /oak|spruce|birch|jungle|acacia|dark_oak|mangrove|cherry|planks|log|wood/i, color: "#9a6a35" },
  { test: /glass|ice/i, color: "#9ed3e6" },
  { test: /water/i, color: "#2b75d6" },
  { test: /lava|magma/i, color: "#e26822" },
  { test: /gold|yellow/i, color: "#d9b33f" },
  { test: /diamond|cyan|prismarine/i, color: "#45c7bf" },
  { test: /redstone|red_/i, color: "#b94343" },
  { test: /black|obsidian|coal|basalt/i, color: "#303038" },
  { test: /white|quartz|snow|wool/i, color: "#d8dde3" },
];
/** EXACT block colours (2026-08-10, "it should be 1:1"): the regex buckets
 *  above are a fallback, but ~30 buckets painted a white-and-dark-wood house
 *  grey-brown. This table carries each common block's real average texture
 *  colour; derived blocks (stairs/slabs/walls/fences of a material) resolve to
 *  their base material below. */
const MC_EXACT: Record<string, string> = {
  stone: "#7d7d7d", cobblestone: "#797979", mossy_cobblestone: "#68785c", smooth_stone: "#9fa0a0",
  stone_bricks: "#7a7a7a", mossy_stone_bricks: "#74796a", cracked_stone_bricks: "#767676", chiseled_stone_bricks: "#777777",
  deepslate: "#4c4c50", cobbled_deepslate: "#4d4d51", polished_deepslate: "#48484c", deepslate_bricks: "#464649", deepslate_tiles: "#36363a",
  granite: "#956755", polished_granite: "#9a6b59", diorite: "#bcbcbd", polished_diorite: "#c3c3c5",
  andesite: "#888a8a", polished_andesite: "#848786", tuff: "#6c6e67", calcite: "#e0e1dc", dripstone_block: "#866a5c",
  bedrock: "#565656", obsidian: "#0f0b19", crying_obsidian: "#21063d",
  dirt: "#866043", coarse_dirt: "#77553b", rooted_dirt: "#90674c", grass_block: "#74a044", podzol: "#6a4c35",
  mud: "#4c3f33", muddy_mangrove_roots: "#463b2d", clay: "#a0a7b3", gravel: "#7f7c7a", dirt_path: "#977e48", farmland: "#693b21",
  sand: "#dbd3a0", sandstone: "#d9cf9c", smooth_sandstone: "#dcd2a2", red_sand: "#b8602c", red_sandstone: "#b25a25",
  moss_block: "#596e2d", mycelium: "#6f6265", snow_block: "#f9fefe", ice: "#91b8fe", packed_ice: "#84a5e4", blue_ice: "#74a9fd",
  oak_planks: "#b8945f", spruce_planks: "#735531", birch_planks: "#c8b77a", jungle_planks: "#b88764",
  acacia_planks: "#ba6337", dark_oak_planks: "#4f3218", mangrove_planks: "#773934", cherry_planks: "#e2b6af",
  bamboo_planks: "#c4a94c", crimson_planks: "#7e3a56", warped_planks: "#3a8e8c",
  oak_log: "#745a36", spruce_log: "#3a2716", birch_log: "#d5d2c8", jungle_log: "#57431b",
  acacia_log: "#696259", dark_oak_log: "#3b2712", mangrove_log: "#583839", cherry_log: "#35211f",
  oak_leaves: "#4d6a28", spruce_leaves: "#2e4c2e", birch_leaves: "#6a8a46", jungle_leaves: "#4a6b23",
  acacia_leaves: "#5b7d29", dark_oak_leaves: "#3f5822", mangrove_leaves: "#4b6b26", cherry_leaves: "#e8b6cf", azalea_leaves: "#5f7628",
  bricks: "#96513c", packed_mud: "#8f6b50", mud_bricks: "#89684f",
  terracotta: "#985e43", glass: "#a8cfd4", tinted_glass: "#2b262e", glowstone: "#d7ba67", sea_lantern: "#c7d3cb", shroomlight: "#f2974b",
  netherrack: "#6f3634", nether_bricks: "#2c161a", red_nether_bricks: "#460709", soul_sand: "#523e33", soul_soil: "#4b3a2f",
  blackstone: "#2a252c", polished_blackstone: "#35313a", polished_blackstone_bricks: "#312d35", gilded_blackstone: "#382b27",
  basalt: "#4d4f4f", smooth_basalt: "#48484d", end_stone: "#dbde9e", end_stone_bricks: "#dade9f", purpur_block: "#a97ba9",
  quartz_block: "#ece9e2", smooth_quartz: "#ece9e2", chiseled_quartz_block: "#e7e4dc", quartz_bricks: "#eae7e0", quartz_pillar: "#ebe8e1",
  prismarine: "#63a58c", prismarine_bricks: "#63ab9d", dark_prismarine: "#335e4c",
  iron_block: "#d8d8d8", gold_block: "#f6d33e", diamond_block: "#61dbd4", emerald_block: "#2fb556", netherite_block: "#443f41",
  copper_block: "#c06c50", exposed_copper: "#a17e68", weathered_copper: "#6c9a6f", oxidized_copper: "#53a486", raw_copper_block: "#9a6650",
  coal_block: "#101010", lapis_block: "#1f438c", redstone_block: "#a91705", amethyst_block: "#8662bf",
  bookshelf: "#a5824f", crafting_table: "#7d5d38", furnace: "#6d6c6c", hay_block: "#a68c0c", melon: "#6d901e", pumpkin: "#c07615",
  sponge: "#c3c14b", tnt: "#87301b", cactus: "#58822d", magma_block: "#8e3f20", ancient_debris: "#5e4139",
  white_concrete: "#cfd5d6", orange_concrete: "#e06101", magenta_concrete: "#a9309f", light_blue_concrete: "#2489c7",
  yellow_concrete: "#f1af15", lime_concrete: "#5ea918", pink_concrete: "#d5658f", gray_concrete: "#36393d",
  light_gray_concrete: "#7d7d73", cyan_concrete: "#157788", purple_concrete: "#64209c", blue_concrete: "#2d2f8f",
  brown_concrete: "#603b1f", green_concrete: "#495b24", red_concrete: "#8e2121", black_concrete: "#080a0f",
  white_wool: "#e9ecec", orange_wool: "#f07613", magenta_wool: "#bd44b3", light_blue_wool: "#3aafd9",
  yellow_wool: "#f8c627", lime_wool: "#70b919", pink_wool: "#ed8dac", gray_wool: "#3e4447",
  light_gray_wool: "#8e8e86", cyan_wool: "#158991", purple_wool: "#792aac", blue_wool: "#35399d",
  brown_wool: "#724728", green_wool: "#546d1b", red_wool: "#a12722", black_wool: "#141519",
  white_terracotta: "#d1b2a1", orange_terracotta: "#a15325", magenta_terracotta: "#95576c", light_blue_terracotta: "#706c8a",
  yellow_terracotta: "#ba8523", lime_terracotta: "#677534", pink_terracotta: "#a04d4e", gray_terracotta: "#392a24",
  light_gray_terracotta: "#876a61", cyan_terracotta: "#565b5b", purple_terracotta: "#764656", blue_terracotta: "#4a3b5b",
  brown_terracotta: "#4d3323", green_terracotta: "#4c532a", red_terracotta: "#8e3c2e", black_terracotta: "#251610",
  white_stained_glass: "#e4e4e4", light_gray_stained_glass: "#909090", gray_stained_glass: "#4c4c4c", black_stained_glass: "#191919",
  brown_stained_glass: "#664c33", red_stained_glass: "#993333", orange_stained_glass: "#d87f33", yellow_stained_glass: "#e5e533",
  lime_stained_glass: "#7fcc19", green_stained_glass: "#667f33", cyan_stained_glass: "#4c7f99", light_blue_stained_glass: "#6699d8",
  blue_stained_glass: "#334cb2", purple_stained_glass: "#7f3fb2", magenta_stained_glass: "#b24cd8", pink_stained_glass: "#f27fa5",
  iron_bars: "#828383", chain: "#31363f", lantern: "#6a5b54", water: "#3f76e4",
};
/** Resolve a derived block (stairs/slab/wall/fence/pane/…) to its base
 *  material's exact colour: oak_stairs → oak_planks, stone_brick_wall →
 *  stone_bricks, smooth_quartz_slab → smooth_quartz. */
function exactColor(short: string): string | null {
  const direct = MC_EXACT[short];
  if (direct) return direct;
  const base = short.replace(/_(stairs|slab|wall|fence|gate|pane|bars|pillar|button|pressure_plate)$/, "").replace(/^(stripped|smooth|polished|cut|chiseled)_/, (m) => (MC_EXACT[short.replace(/_(stairs|slab|wall|fence|gate|pane|bars)$/, "")] ? m : m));
  for (const cand of [base, `${base}s`, `${base}_block`, `${base}_planks`]) {
    if (MC_EXACT[cand]) return MC_EXACT[cand];
  }
  // stripped logs/wood: a touch lighter than the bark colour
  const stripped = /^stripped_(.+?)_(log|wood)$/.exec(short);
  if (stripped) {
    const planks = MC_EXACT[`${stripped[1]}_planks`];
    if (planks) return planks;
  }
  const woodAlias = /^(.+?)_wood$/.exec(short);
  if (woodAlias && MC_EXACT[`${woodAlias[1]}_log`]) return MC_EXACT[`${woodAlias[1]}_log`];
  return null;
}
export function mcNameColor(name: string): string {
  const short = name.replace(/^minecraft:/, "").toLowerCase();
  const exact = exactColor(short);
  if (exact) return exact;
  for (const c of MC_COLORS) if (c.test.test(name)) return c.color;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360},36%,54%)`;
}
export function mcSolid(name: string): boolean {
  // Anchor the short words: a bare /light/ dropped every light_blue_* and
  // light_gray_* block, /mushroom/ dropped mushroom BLOCKS (giant-mushroom
  // builds vanished), /flower/ dropped flowering_azalea_leaves, /water/
  // dropped water_cauldron. Those all read as "random blocks missing" after
  // import.
  // command_block/structure_block/jigsaw/moving_piston are MAP MACHINERY —
  // adventure maps hide whole vaults of them underground, and importing them
  // painted the wiring room into the build (1435 chain_command_blocks in the
  // first real-world test).
  // The second alternation row is the FLOWER/PLANT family that dodges the
  // generic "flower$" suffix — blue_orchid, poppy, rose_bush, lilac, crops…
  // they rendered as random hash-coloured speckles across every grassland.
  // DOORS (and trapdoors — `door$` covers both) and fence gates are OPENINGS,
  // not walls: imported as solid cubes they sealed every building shut ("I
  // can't enter the house"). The game engine has no openable doors, so the
  // passage stays open.
  return !/(^|:)air$|cave_air|void_air|water$|lava$|barrier|structure_void|(^|:)light$|torch$|lantern|button|lever|pressure_plate|sign$|banner|rail$|tripwire|redstone_wire|flower$|flower_pot|sapling|grass$|fern|mushroom$|kelp|seagrass|vine|coral(?!_block)|carpet|snow$|command_block|structure_block|jigsaw|moving_piston|poppy|dandelion|orchid|allium|bluet|tulip|daisy|wither_rose|pink_petals|lily|bush$|lilac|peony|wheat$|carrots$|potatoes$|beetroots$|melon_stem$|pumpkin_stem$|sugar_cane|cobweb|(^|:)bamboo$|nether_wart$|_bud$|sculk_vein|glow_lichen|hanging_roots|spore_blossom|dripleaf|pointed_dripstone|cocoa$|(^|:)frogspawn$|turtle_egg|candle(s|$)|door$|fence_gate$/i.test(name);
}
/** Vegetation that marks a LANDSCAPED scene (auto-centre scoring): a build
 *  surrounded by grass and trees is what a map's screenshot shows; a windowless
 *  vault of concrete scores structure but never vegetation. */
export function mcVegetation(name: string): boolean {
  return /grass_block|leaves|podzol|moss_block|azalea/i.test(name);
}
/** NATURAL tree wood (un-stripped log/wood): mcStructure counts it, but for
 *  scene detection it is scenery — on a forested world every chunk holds
 *  trunks, and treating them as "structure" welded the whole map into one
 *  scene. Crafted wood (stripped_*, planks) stays structural. */
export function mcNaturalWood(name: string): boolean {
  return /(^|:)(?!stripped_)[a-z_]*(log|wood)$/i.test(name);
}
export function mcStructure(name: string): boolean {
  // `bed$`, not `bed`: BEDROCK matched the bare substring, which made every
  // chunk of a survival world "structural" (the bedrock floor is everywhere)
  // and welded whole worlds into one detected scene.
  return /brick|cobble|plank|log|wood|glass|fence|wall|stairs|slab|door|trapdoor|iron|gold|wool|terracotta|concrete|quartz|tile|chain|bar|spawner|command_block|redstone|repeater|comparator|piston|dispenser|dropper|chest|bookshelf|ladder|bed$|table|anvil/i.test(name) &&
    !/grass_block|dirt|stone$|deepslate$|tuff$|andesite$|diorite$|granite$|sand$|gravel$|ore$|bedrock/i.test(name);
}
export function paletteName(entry: unknown): string {
  const e = entry as { Name?: unknown; name?: unknown };
  return String(e?.Name ?? e?.name ?? "minecraft:stone");
}
export function blockStateIndex(data: bigint[] | undefined, i: number, bits: number): number {
  if (!data?.length) return 0;
  // Minecraft 1.16+ (Anvil, DataVersion ≥ 2529) packs indices NON-spanning: each
  // 64-bit long holds floor(64/bits) indices with the high bits left as padding,
  // and an index never straddles two longs. (Pre-1.16 packed continuously — the
  // old spanning formula read garbage for modern worlds → a flat uniform layer.)
  // Detect the layout from the array length so both eras work.
  const perLong = Math.floor(64 / bits);
  const spanningLen = Math.ceil((4096 * bits) / 64);
  const mask = (1n << BigInt(bits)) - 1n;
  if (data.length <= spanningLen) {
    // legacy continuous packing (pre-1.16)
    const bit = BigInt(i * bits);
    const li = Number(bit >> 6n);
    const shift = Number(bit & 63n);
    let v = (BigInt.asUintN(64, data[li] ?? 0n) >> BigInt(shift)) & mask;
    const spill = shift + bits - 64;
    if (spill > 0) v |= (BigInt.asUintN(64, data[li + 1] ?? 0n) & ((1n << BigInt(spill)) - 1n)) << BigInt(bits - spill);
    return Number(v);
  }
  // 1.16+ non-spanning
  const li = Math.floor(i / perLong);
  const within = i % perLong;
  return Number((BigInt.asUintN(64, data[li] ?? 0n) >> BigInt(within * bits)) & mask);
}

/** One decoded schematic-style file: dimensions + an iterator over SOLID
 *  blocks (already filtered + coloured). Covers the three formats people
 *  actually have: classic MCEdit `.schematic` (byte ids), Sponge `.schem`
 *  v1/v2/v3 (WorldEdit's modern export — palette compound + varint data),
 *  and vanilla structure-block `.nbt` (size/palette/blocks). The old importer
 *  ONLY read classic MCEdit, while the file picker accepted `.nbt` — every
 *  structure-block export failed with "Unsupported schematic". */
export interface DecodedSchematic {
  W: number; H: number; L: number;
  each(cb: (x: number, y: number, z: number, color: string) => void): void;
}
export function decodeAnySchematic(rootIn: Record<string, unknown>): DecodedSchematic | null {
  // Sponge v3 wraps everything in a "Schematic" compound
  const wrapped = rootIn.Schematic;
  const root = (wrapped && typeof wrapped === "object" ? wrapped : rootIn) as Record<string, unknown>;
  // vanilla structure block .nbt: size [x,y,z], palette [{Name}], blocks [{pos, state}]
  const size = root.size;
  if (Array.isArray(size) && size.length === 3 && Array.isArray(root.blocks) && Array.isArray(root.palette)) {
    const W = Number(size[0]), H = Number(size[1]), L = Number(size[2]);
    if (!W || !H || !L) return null;
    const names = (root.palette as unknown[]).map((p) => paletteName(p));
    const blocks = root.blocks as { pos?: unknown; state?: unknown }[];
    return { W, H, L, each: (cb) => {
      for (const b of blocks) {
        const pos = b.pos as number[] | undefined;
        if (!Array.isArray(pos) || pos.length !== 3) continue;
        const name = names[Number(b.state) | 0] ?? "minecraft:air";
        if (!mcSolid(name)) continue;
        cb(Number(pos[0]), Number(pos[1]), Number(pos[2]), mcNameColor(name));
      }
    } };
  }
  const W = Number(root.Width), H = Number(root.Height), L = Number(root.Length);
  if (!W || !H || !L) return null;
  // Sponge .schem: v1/v2 keep Palette+BlockData at the root, v3 under Blocks{Palette,Data}
  const sub = root.Blocks && !(root.Blocks instanceof Uint8Array) ? root.Blocks as Record<string, unknown> : undefined;
  const bd = (root.BlockData ?? sub?.Data) as unknown;
  const palComp = (root.Palette ?? sub?.Palette) as unknown;
  if (bd instanceof Uint8Array && palComp && typeof palComp === "object" && !Array.isArray(palComp)) {
    const names: string[] = [];
    for (const [name, idx] of Object.entries(palComp as Record<string, unknown>)) {
      names[Number(idx)] = name.replace(/\[.*$/, ""); // strip [facing=…] block-state props
    }
    return { W, H, L, each: (cb) => {
      const n = W * H * L;
      let off = 0;
      for (let i = 0; i < n && off < bd.length; i++) {
        // unsigned LEB128 varint — palette indices past 127 use multi-byte encoding
        let v = 0, shift = 0, byte = 0;
        do { byte = bd[off++]; v |= (byte & 0x7f) << shift; shift += 7; } while (byte & 0x80 && off < bd.length);
        const name = names[v] ?? "minecraft:air";
        if (!mcSolid(name)) continue;
        // Sponge order: index = (y*L + z)*W + x
        cb(i % W, Math.floor(i / (W * L)), Math.floor(i / W) % L, mcNameColor(name));
      }
    } };
  }
  // classic MCEdit .schematic: flat byte ids, same (y*L + z)*W + x order
  const B = root.Blocks;
  if (B instanceof Uint8Array) {
    return { W, H, L, each: (cb) => {
      for (let y = 0; y < H; y++) for (let z = 0; z < L; z++) for (let x = 0; x < W; x++) {
        const bi = (y * L + z) * W + x;
        if (bi >= B.length) break;
        const v = B[bi] & 0xff;
        if (v) cb(x, y, z, mcBlockColor(v));
      }
    } };
  }
  return null;
}

/** One scanned chunk: combined score `n` (structure weighted 3x vegetation),
 *  pure structure hits `structN` (scene membership — vegetation-only chunks
 *  must never seed or bridge scenes), and the Y range the hits span. */
export interface ScanChunk { chunkX: number; chunkZ: number; n: number; structN: number; yMin: number; yMax: number }
/** One detected build SCENE: weighted centre, footprint span, Y range, score. */
export interface Scene { x: number; z: number; span: number; yMin: number; yMax: number; weight: number }
/**
 * Group scanned chunks into distinct build scenes. Adventure maps scatter
 * SEVERAL builds (intro road, the house, machinery vaults) hundreds of blocks
 * apart — a single global "densest chunk" import can only ever show one of
 * them, and before vegetation scoring it reliably picked the hidden
 * command-block vault. Greedy: take the highest-score unclaimed chunk, absorb
 * everything within `radius` chunks, repeat. Sorted best-first.
 */
export function detectScenes(chunks: ScanChunk[], radius = 8, maxScenes = 12): Scene[] {
  const remaining = new Map<string, ScanChunk>();
  // vegetation-only chunks are scenery, not scenes: on a natural-terrain world
  // they carpet everything, and letting them seed/join/bridge clusters merged
  // the whole map into one 2km blob (Savior test world). They still counted
  // toward the mixed chunks' scores during the scan.
  for (const c of chunks) if (c.structN > 0) remaining.set(`${c.chunkX}:${c.chunkZ}`, c);
  interface Raw { wx: number; wz: number; minX: number; maxX: number; minZ: number; maxZ: number; yMins: number[]; yMax: number; weight: number }
  const raws: Raw[] = [];
  while (remaining.size > 0 && raws.length < maxScenes * 4) {
    let best: ScanChunk | null = null;
    for (const c of remaining.values()) if (!best || c.n > best.n) best = c;
    if (!best) break;
    const near: ScanChunk[] = [];
    for (const c of remaining.values()) {
      if (Math.hypot(c.chunkX - best.chunkX, c.chunkZ - best.chunkZ) <= radius) near.push(c);
    }
    for (const c of near) remaining.delete(`${c.chunkX}:${c.chunkZ}`);
    const weight = near.reduce((s, c) => s + c.n, 0);
    if (weight <= 0) continue;
    const r: Raw = {
      wx: near.reduce((s, c) => s + (c.chunkX * 16 + 8) * c.n, 0),
      wz: near.reduce((s, c) => s + (c.chunkZ * 16 + 8) * c.n, 0),
      minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity,
      yMins: near.map((c) => c.yMin), yMax: -Infinity, weight,
    };
    for (const c of near) {
      r.minX = Math.min(r.minX, c.chunkX * 16); r.maxX = Math.max(r.maxX, c.chunkX * 16 + 16);
      r.minZ = Math.min(r.minZ, c.chunkZ * 16); r.maxZ = Math.max(r.maxZ, c.chunkZ * 16 + 16);
      r.yMax = Math.max(r.yMax, c.yMax);
    }
    raws.push(r);
  }
  // MERGE adjacent clusters: one continuous build larger than `radius` (a
  // castle with its grounds) tiles into several radius-limited clusters —
  // stitch any pair whose footprints come within 48 blocks back together.
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < raws.length && !changed; i++) for (let j = i + 1; j < raws.length; j++) {
      const a = raws[i], b = raws[j];
      const gapX = Math.max(a.minX, b.minX) - Math.min(a.maxX, b.maxX);
      const gapZ = Math.max(a.minZ, b.minZ) - Math.min(a.maxZ, b.maxZ);
      if (Math.max(gapX, gapZ) > 48) continue;
      // never merge into a scene wider than the editor can import at full
      // detail — a 1.2km estate is better as castle + grounds + village scenes
      const unionSpan = Math.max(
        Math.max(a.maxX, b.maxX) - Math.min(a.minX, b.minX),
        Math.max(a.maxZ, b.maxZ) - Math.min(a.minZ, b.minZ));
      if (unionSpan > 640) continue;
      a.wx += b.wx; a.wz += b.wz; a.weight += b.weight;
      a.minX = Math.min(a.minX, b.minX); a.maxX = Math.max(a.maxX, b.maxX);
      a.minZ = Math.min(a.minZ, b.minZ); a.maxZ = Math.max(a.maxZ, b.maxZ);
      a.yMins.push(...b.yMins); a.yMax = Math.max(a.yMax, b.yMax);
      raws.splice(j, 1);
      changed = true;
      break;
    }
  }
  const scenes: Scene[] = raws.map((r) => {
    // yMin is a PERCENTILE, not the true min: one machinery chunk buried at
    // bedrock under an otherwise surface-level scene dragged every import 60
    // layers deep. yMax stays the true max — a castle spire lives in few
    // chunks and must not be cut.
    const yMins = [...r.yMins].sort((x, y) => x - y);
    return {
      x: Math.round(r.wx / r.weight), z: Math.round(r.wz / r.weight),
      span: Math.max(r.maxX - r.minX, r.maxZ - r.minZ),
      yMin: yMins[Math.min(yMins.length - 1, Math.floor(yMins.length * 0.2))] - 2,
      yMax: r.yMax, weight: r.weight,
    };
  });
  scenes.sort((a, b) => b.weight - a.weight);
  // drop trailing specks (a lone lamppost 300m away is not a scene)
  const cut = scenes.length ? Math.max(500, scenes[0].weight * 0.03) : 0;
  return scenes.filter((s) => s.weight >= cut).slice(0, maxScenes);
}

/** Halve a voxel grid along every axis (first-nonzero wins per 2×2×2 block).
 *  Used to auto-coarsen an import that merges to more boxes than the editor
 *  can hold — a complete coarser build beats a full-resolution one with holes. */
export function downsampleGrid(grid: Uint8Array, nx: number, ny: number, nz: number): { grid: Uint8Array; nx: number; ny: number; nz: number } {
  const hx = Math.max(1, Math.ceil(nx / 2)), hy = Math.max(1, Math.ceil(ny / 2)), hz = Math.max(1, Math.ceil(nz / 2));
  const out = new Uint8Array(hx * hy * hz);
  // TOP-DOWN: the highest cell in each 2×2×2 block wins the colour — bottom-up
  // first-wins painted every coarsened surface with the block UNDER it (grass
  // hills came out dirt-brown across a whole world).
  for (let by = ny - 1; by >= 0; by--) for (let bz = 0; bz < nz; bz++) for (let bx = 0; bx < nx; bx++) {
    const v = grid[(by * nz + bz) * nx + bx];
    if (!v) continue;
    const oi = ((by >> 1) * hz + (bz >> 1)) * hx + (bx >> 1);
    if (!out[oi]) out[oi] = v;
  }
  return { grid: out, nx: hx, ny: hy, nz: hz };
}

/** Sub-cell box in unit-cube space: offset from the cell's min corner + size. */
export interface SubBox { dx: number; dy: number; dz: number; sx: number; sy: number; sz: number }
/** What to do with one palette entry: skip it, fill the whole cell, or emit
 *  shaped sub-boxes (slab/stair/fence/pane). Classified ONCE per palette entry
 *  per section — the old code ran the name regexes on every single block. */
export interface BlockAction { kind: "skip" | "full" | "extra"; color: string; struct: boolean; glass?: boolean; boxes?: SubBox[] }
const SLAB_BOTTOM: SubBox = { dx: 0, dy: 0, dz: 0, sx: 1, sy: 0.5, sz: 1 };
const SLAB_TOP: SubBox = { dx: 0, dy: 0.5, dz: 0, sx: 1, sy: 0.5, sz: 1 };
/**
 * SHAPED blocks (2026-08-10, "it should be 1:1"): slabs and stairs used to
 * import as full cubes — every staircase became an unclimbable 1m wall and
 * every slab roof doubled in thickness. Fences/walls/panes/bars became solid
 * cubes too. Block-state Properties (slab type, stair half+facing, fence
 * connection sides) are read from the palette entry so the shape survives.
 */
export function classifyBlock(entry: unknown): BlockAction {
  const name = paletteName(entry);
  if (!mcSolid(name)) return { kind: "skip", color: "", struct: false };
  const color = mcNameColor(name);
  const struct = mcStructure(name);
  // glass is tagged so the game renders it TRANSLUCENT (colour classification
  // alone sent stained glass to an opaque family)
  const glass = /glass/i.test(name) || /(^|:)ice$/i.test(name);
  const props = ((entry as { Properties?: unknown }).Properties ?? {}) as Record<string, unknown>;
  if (/_slab$/i.test(name)) {
    const t = String(props.type ?? "bottom");
    if (t === "double") return { kind: "full", color, struct, glass };
    return { kind: "extra", color, struct, glass, boxes: [t === "top" ? SLAB_TOP : SLAB_BOTTOM] };
  }
  if (/_stairs$/i.test(name)) {
    const half = String(props.half ?? "bottom");
    const facing = String(props.facing ?? "north");
    // base slab + a half-depth riser on the `facing` side (the stair's tall
    // back) — walkable at the movement code's step height, unlike a full cube
    const riser: SubBox = {
      dx: facing === "east" ? 0.5 : 0,
      dy: half === "top" ? 0 : 0.5,
      dz: facing === "south" ? 0.5 : 0,
      sx: facing === "east" || facing === "west" ? 0.5 : 1,
      sy: 0.5,
      sz: facing === "north" || facing === "south" ? 0.5 : 1,
    };
    return { kind: "extra", color, struct, glass, boxes: [half === "top" ? SLAB_TOP : SLAB_BOTTOM, riser] };
  }
  if (/fence$|_wall$|(^|:)iron_bars$|glass_pane$/i.test(name)) {
    const w = /glass_pane|iron_bars/i.test(name) ? 0.2 : /_wall$/i.test(name) ? 0.35 : 0.28;
    const c = (1 - w) / 2;
    const boxes: SubBox[] = [{ dx: c, dy: 0, dz: c, sx: w, sy: 1, sz: w }];
    const on = (k: string): boolean => { const v = String(props[k] ?? "false"); return v !== "false" && v !== "none"; };
    if (on("north")) boxes.push({ dx: c, dy: 0, dz: 0, sx: w, sy: 1, sz: c });
    if (on("south")) boxes.push({ dx: c, dy: 0, dz: c + w, sx: w, sy: 1, sz: c });
    if (on("west")) boxes.push({ dx: 0, dy: 0, dz: c, sx: c, sy: 1, sz: w });
    if (on("east")) boxes.push({ dx: c + w, dy: 0, dz: c, sx: c, sy: 1, sz: w });
    return { kind: "extra", color, struct, glass, boxes };
  }
  return { kind: "full", color, struct, glass };
}

export function makePalette(): { palette: string[]; colorIdx: (c: string) => number } {
  const palette: string[] = [];
  const idx = new Map<string, number>();
  return {
    palette,
    colorIdx: (c: string): number => {
      let i = idx.get(c);
      if (i === undefined) {
        if (palette.length >= 255) return 1; // Uint8 grid — palette full, reuse the first colour
        palette.push(c);
        i = palette.length;
        idx.set(c, i);
      }
      return i;
    },
  };
}

export const MAX_GRID_CELLS = 64_000_000; // 64 MB Uint8Array budget; import step auto-raises above it

export interface GridSlab { bx0: number; bx1: number; by0: number; by1: number; bz0: number; bz1: number; color: number; score: number }

/**
 * Greedy-merge a dense voxel grid (0 = empty, else 1-based palette index) into
 * boxes. Unlike the old string-key pipeline there is no raw cell budget: every
 * occupied cell is covered by exactly one box (fully enclosed interior cells
 * may be culled when cullInterior — they are invisible AND sealed, so every
 * visible face is identical). Result is sorted biggest-first so a downstream
 * cap drops the least map first.
 */
export function mergeGrid(grid: Uint8Array, nx: number, ny: number, nz: number, cullInterior: boolean): GridSlab[] {
  const at = (bx: number, by: number, bz: number): number =>
    bx < 0 || bx >= nx || by < 0 || by >= ny || bz < 0 || bz >= nz ? 0 : grid[(by * nz + bz) * nx + bx];
  const midX = nx / 2, midZ = nz / 2;
  interface Cand { bx: number; bz: number; color: number; exposed: number; d: number }
  const byLayer = new Map<number, Map<number, Cand>>();
  for (let by = 0; by < ny; by++) {
    let layer: Map<number, Cand> | null = null;
    for (let bz = 0; bz < nz; bz++) for (let bx = 0; bx < nx; bx++) {
      const c = grid[(by * nz + bz) * nx + bx];
      if (!c) continue;
      const exposed = (at(bx + 1, by, bz) ? 0 : 1) + (at(bx - 1, by, bz) ? 0 : 1)
        + (at(bx, by + 1, bz) ? 0 : 1) + (at(bx, by - 1, bz) ? 0 : 1)
        + (at(bx, by, bz + 1) ? 0 : 1) + (at(bx, by, bz - 1) ? 0 : 1);
      if (cullInterior && exposed === 0) continue;
      if (!layer) { layer = new Map(); byLayer.set(by, layer); }
      layer.set(bz * nx + bx, { bx, bz, color: c, exposed, d: Math.hypot(bx - midX, bz - midZ) });
    }
  }
  const slabs: GridSlab[] = [];
  for (const [by, layer] of byLayer) {
    const unused = new Set(layer.keys());
    const ordered = [...layer.values()].sort((a, b) => b.exposed - a.exposed || a.d - b.d);
    for (const start of ordered) {
      if (!unused.has(start.bz * nx + start.bx)) continue;
      // grow the rectangle in BOTH directions — one-way growth from a mid-layer
      // seed fragments a uniform layer into several boxes for no reason
      const fits = (bx: number, bz: number): boolean => {
        const k = bz * nx + bx;
        return unused.has(k) && layer.get(k)!.color === start.color;
      };
      let bx0 = start.bx, bx1 = start.bx;
      while (bx1 + 1 < nx && fits(bx1 + 1, start.bz)) bx1++;
      while (bx0 - 1 >= 0 && fits(bx0 - 1, start.bz)) bx0--;
      const rowFits = (bz: number): boolean => {
        for (let bx = bx0; bx <= bx1; bx++) if (!fits(bx, bz)) return false;
        return true;
      };
      let bz0 = start.bz, bz1 = start.bz;
      while (bz1 + 1 < nz && rowFits(bz1 + 1)) bz1++;
      while (bz0 - 1 >= 0 && rowFits(bz0 - 1)) bz0--;
      for (let bz = bz0; bz <= bz1; bz++) for (let bx = bx0; bx <= bx1; bx++) unused.delete(bz * nx + bx);
      slabs.push({ bx0, bx1, by0: by, by1: by, bz0, bz1, color: start.color, score: start.exposed * 20 - start.d });
    }
  }
  const stacks = new Map<string, GridSlab[]>();
  for (const s of slabs) {
    const key = `${s.bx0}:${s.bx1}:${s.bz0}:${s.bz1}:${s.color}`;
    const arr = stacks.get(key) ?? [];
    arr.push(s);
    stacks.set(key, arr);
  }
  const merged: GridSlab[] = [];
  for (const arr of stacks.values()) {
    arr.sort((a, b) => a.by0 - b.by0);
    let cur: GridSlab | null = null;
    for (const s of arr) {
      if (cur && s.by0 === cur.by1 + 1) cur.by1 = s.by1;
      else { if (cur) merged.push(cur); cur = { ...s }; }
    }
    if (cur) merged.push(cur);
  }
  merged.sort((a, b) =>
    (b.bx1 - b.bx0 + 1) * (b.by1 - b.by0 + 1) * (b.bz1 - b.bz0 + 1) - (a.bx1 - a.bx0 + 1) * (a.by1 - a.by0 + 1) * (a.bz1 - a.bz0 + 1) || b.score - a.score);
  return merged;
}
